/**
 * PowerPoint add-in constants, grouped by feature.
 *
 * User-facing strings here are the approved copy from the AiVA for PowerPoint
 * Feature & Function Specification v1.1.0.0. Change them only when the spec
 * changes — QA asserts on them verbatim.
 */

/* ═══════════════════════ DI-02 · List speaker notes (AIVA-6756) ═══════════════════════ */

/** Longest notes preview shown per slide before an ellipsis (spec G-7). */
export const NOTES_PREVIEW_MAX_CHARS = 400;

export const SPEAKER_NOTES_COPY = {
  /** Posted in the thread while the deck is being walked. */
  READING: "Reading speaker notes…",
  /** Every slide readable, none has notes (G-7). */
  NONE_SET: "No speaker notes set on any slide.",
  /** No slide's notes could be read on this host (G-7). */
  NONE_READABLE: "AiVA can't read speaker notes in this version of PowerPoint.",
  /** Not in the spec: the presentation has no slides at all. */
  EMPTY_DECK: "This presentation has no slides.",
} as const;

/** "3 slides with notes:" / "1 slide with notes:" */
export function speakerNotesHeader(count: number): string {
  return `${count} ${count === 1 ? "slide" : "slides"} with notes:`;
}

/** Partial-read footnote (G-7): "Notes on 2 slides couldn't be read in this version of PowerPoint." */
export function speakerNotesPartialFootnote(unreadable: number): string {
  return `Notes on ${unreadable} ${unreadable === 1 ? "slide" : "slides"} couldn't be read in this version of PowerPoint.`;
}

/* ═══════════════════════ DI-01 · Summarise the deck (AIVA-6811) ═══════════════════════ */

/** Deck text sent with a summary turn (spec: "capped at ~12,000 characters"). */
export const DECK_CONTEXT_CHAR_CAP = 12_000;

/** Bullets when the prompt asks for no number, and the most it may ask for. */
export const DEFAULT_SUMMARY_BULLETS = 6;
export const MAX_SUMMARY_BULLETS = 12;

/** The deck-summary skill, forced so a Deckorate pick can't take a turn that carries deck text. */
export const DECK_SUMMARY_SKILL_KEY = "ppt_summarise_deck";

export const DECK_SUMMARY_COPY = {
  READING: "Reading deck and summarising…",
  /** No slide has text: answered locally, no AI call (spec). */
  EMPTY_DECK: "The deck is empty.",
  UNREADABLE: "AiVA couldn't read this presentation. Try again, or save the deck and retry.",
} as const;

/** "(4 bullets)" / "(1 bullet)" */
export function deckSummaryBulletLabel(count: number): string {
  return `(${count} ${count === 1 ? "bullet" : "bullets"})`;
}

/** G-9 phase 1: "This deck is long, so AiVA summarised the first 28 slides." */
export function deckSummaryTruncationNote(slidesSummarised: number): string {
  return `This deck is long, so AiVA summarised the first ${slidesSummarised} ${slidesSummarised === 1 ? "slide" : "slides"}.`;
}

/** AC-5: "Summary failed: <what happened>". */
export function deckSummaryFailedToast(reason: string): string {
  return `Summary failed: ${reason}`;
}

/* ═══════════════════════ SC-06 · Rephrase the current slide (AIVA-6810) ═══════════════════════ */

export const REPHRASE_COPY = {
  READING: "Reading slide…",
  NO_TEXT: "The current slide has no text to rephrase.",
  /** AC-3: the reply wasn't the JSON array. */
  UNPARSEABLE: "Couldn't parse the rewrite response. Try again.",
  /** Not in the spec: the AI call itself failed. */
  REQUEST_FAILED: "AiVA couldn't rephrase this slide. Try again.",
  /** Not in the spec: every shape already reads as asked. */
  NO_CHANGES: "This slide already reads well, so AiVA has no rewrites to suggest.",
  CANCELLED: "Rewrite cancelled.",
  /** G-3 / AC-5. */
  STALE: "The slide changed while you were reviewing. Ask again to get fresh rewrites.",
  /** Not in the spec: the write itself failed. */
  WRITE_FAILED: "AiVA couldn't update the slide. Nothing was changed. Try again.",
} as const;

/** "Proposed 2 rewrites for the current slide:" */
export function rephraseProposedHeader(count: number): string {
  return `Proposed ${count} ${count === 1 ? "rewrite" : "rewrites"} for the current slide:`;
}

/** "18 shapes need no change" — the folded list of shapes the AI left as they were (AIVA-9184). */
export function rephraseUnchangedSummary(count: number): string {
  return `${count} ${count === 1 ? "shape needs" : "shapes need"} no change`;
}

/** "Applied 2 rewrites." */
export function rephraseAppliedMessage(count: number): string {
  return `Applied ${count} ${count === 1 ? "rewrite" : "rewrites"}.`;
}
